import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Typography,
  Paper,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import ListIcon from '@mui/icons-material/List';
import ConstructionIcon from '@mui/icons-material/Construction';

const ToolProjectsTutorial = () => {
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const steps = [
    {
      label: 'Search for Tools',
      description: `Start by creating a project with a descriptive name (e.g., "Kitchen Renovation" or "Deck Building"). Then enter your tool search query describing what tools you need.`,
      image: '/tool_search.png',
      tip: 'Be specific about the task you need tools for to get the most relevant recommendations.'
    },
    {
      label: 'View Tool Recommendations',
      description: `After searching, you'll see tool recommendations specifically tailored to your project needs, including a project summary and detailed tool suggestions.`,
      image: '/tool_recs.png',
      tip: 'Each tool includes its name, type, description, and a link to view or purchase it.'
    },
    {
      label: 'Access Your Tool Projects',
      description: `Select your project from the dropdown menu in the "Your DIY Tool Projects" section to view saved tool recommendations for that project.`,
      image: '/select_tool_project.png',
      tip: 'Your most recent tool search will appear at the top of the projects list.'
    }
  ];

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label="Help"
        color="primary"
        sx={{ 
          position: 'fixed',
          bottom: 20,
          right: 20,
          bgcolor: 'white',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          '&:hover': {
            bgcolor: '#f0f7ff',
          },
          zIndex: 1000,
          width: 60,
          height: 60
        }}
      >
        <HelpOutlineIcon fontSize="large" />
      </IconButton>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#1e3c72',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2
          }}
        >
          {/* Fix: Use span instead of Typography to avoid nesting h tags */}
          <span style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
            How to Use the DIY Tools Feature
          </span>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" paragraph>
              Follow these simple steps to find the perfect tools for your DIY projects:
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 4 }}>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" paragraph>
                      {step.description}
                    </Typography>
                    
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        bgcolor: '#f0f7ff', 
                        borderRadius: 2,
                        mb: 2,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1
                      }}
                    >
                      <InfoIcon color="primary" sx={{ mt: 0.5 }} />
                      <Typography variant="body2" color="primary.dark">
                        <strong>Tip:</strong> {step.tip}
                      </Typography>
                    </Paper>
                    
                    {/* Fix: Use the same image implementation as PersonalProjectsTutorial */}
                    <Paper
                      elevation={3}
                      sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        mb: 2,
                        maxWidth: 500,
                        mx: 'auto'
                      }}
                    >
                      <Box
                        component="img"
                        src={step.image}
                        alt={`Step ${index + 1}: ${step.label}`}
                        sx={{
                          width: '100%',
                          height: 'auto'
                        }}
                        onError={(e) => {
                          // Fallback if image doesn't load
                          e.target.style.display = 'none';
                          e.target.parentElement.style.height = '250px';
                          e.target.parentElement.style.display = 'flex';
                          e.target.parentElement.style.justifyContent = 'center';
                          e.target.parentElement.style.alignItems = 'center';
                          e.target.parentElement.style.backgroundColor = '#f5f5f5';
                          
                          // Create and append placeholder content
                          const placeholderDiv = document.createElement('div');
                          placeholderDiv.style.textAlign = 'center';
                          
                          const icon = document.createElement('div');
                          icon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="#1e3c72" style="margin-bottom: 16px;"><path d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4V7zm-1-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>`;
                          
                          const text = document.createElement('div');
                          text.textContent = `Screenshot placeholder for ${step.label}`;
                          text.style.color = '#666';
                          text.style.fontSize = '14px';
                          
                          placeholderDiv.appendChild(icon);
                          placeholderDiv.appendChild(text);
                          e.target.parentElement.appendChild(placeholderDiv);
                        }}
                      />
                    </Paper>
                  </Box>
                  <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                    <Button
                      disabled={index === 0}
                      onClick={handleBack}
                      variant="outlined"
                      color="primary"
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={index === steps.length - 1 ? handleClose : handleNext}
                      sx={{ 
                        background: index === steps.length - 1 
                          ? 'linear-gradient(90deg, #2a5298 0%, #1e3c72 100%)' 
                          : undefined 
                      }}
                    >
                      {index === steps.length - 1 ? 'Finish' : 'Continue'}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {activeStep === steps.length && (
            <Paper square elevation={0} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                All steps completed! You're ready to start using the DIY Tools feature.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button onClick={handleReset} variant="outlined" color="primary">
                  Review Steps Again
                </Button>
                <Button onClick={handleClose} variant="contained" color="primary">
                  Find DIY Tools
                </Button>
              </Box>
            </Paper>
          )}

          <Divider sx={{ my: 4 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1e3c72' }}>
              Quick Reference Guide
            </Typography>
            <Box 
              sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                gap: 3,
                mt: 2
              }}
            >
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <SearchIcon sx={{ fontSize: 40, color: '#1e3c72' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  1. Search
                </Typography>
                <Typography variant="body2" textAlign="center">
                  Enter project name & describe the tools you need
                </Typography>
              </Paper>
              
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <ConstructionIcon sx={{ fontSize: 40, color: '#1e3c72' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  2. View Recommendations
                </Typography>
                <Typography variant="body2" textAlign="center">
                  See project summary and personalized tool suggestions
                </Typography>
              </Paper>
              
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <ListIcon sx={{ fontSize: 40, color: '#1e3c72' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  3. Select Projects
                </Typography>
                <Typography variant="body2" textAlign="center">
                  Access saved tool recommendations from previous searches
                </Typography>
              </Paper>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ToolProjectsTutorial;